import { useEffect, useState } from 'react'
import { useAuth } from '../context/AuthContext'

const emptyPost = {
  id: null, title: '', slug: '', excerpt: '', content: '', coverImage: '', status: 'draft',
  publishedAt: '', metaTitle: '', metaDescription: '', categoryIds: [], tagIds: []
}

const token = () => localStorage.getItem('tredex_token')

async function readJson(res) {
  const data = await res.json().catch(() => ({}))
  if (!res.ok) throw new Error(data.error || 'Request failed')
  return data
}

function toDateTimeLocal(value) {
  return value ? new Date(value).toISOString().slice(0, 16) : ''
}

export default function AdminBlog() {
  const { user } = useAuth()
  const [posts, setPosts] = useState([])
  const [categories, setCategories] = useState([])
  const [tags, setTags] = useState([])
  const [comments, setComments] = useState([])
  const [tab, setTab] = useState('posts')
  const [message, setMessage] = useState('')
  const [error, setError] = useState('')
  const [editorOpen, setEditorOpen] = useState(false)
  const [post, setPost] = useState(emptyPost)
  const [saving, setSaving] = useState(false)

  const headers = { 'Content-Type': 'application/json', Authorization: `Bearer ${token()}` }
  const notify = (text) => { setMessage(text); setError('') }
  const fail = (err) => { setError(err.message || 'Something went wrong'); setMessage('') }

  const loadAll = async () => {
    try {
      const [postsData, categoriesData, tagsData, commentsData] = await Promise.all([
        fetch('/api/blog/admin/posts', { headers }).then(readJson),
        fetch('/api/blog/admin/categories', { headers }).then(readJson),
        fetch('/api/blog/admin/tags', { headers }).then(readJson),
        fetch('/api/blog/admin/comments', { headers }).then(readJson)
      ])
      setPosts(postsData.posts || [])
      setCategories(categoriesData.categories || [])
      setTags(tagsData.tags || [])
      setComments(commentsData.comments || [])
    } catch (err) {
      fail(err)
    }
  }

  useEffect(() => {
    if (user?.role === 'admin') loadAll()
  }, [user])

  const openNew = () => {
    setPost(emptyPost)
    setEditorOpen(true)
    setError('')
  }

  const openEdit = async (item) => {
    try {
      const preview = await fetch(`/api/blog/admin/posts/${item.id}/preview`, { headers }).then(readJson)
      setPost({
        id: preview.id, title: preview.title || '', slug: preview.slug || '', excerpt: preview.excerpt || '',
        content: preview.content || '', coverImage: preview.cover_image || '', status: preview.status || 'draft',
        publishedAt: toDateTimeLocal(preview.published_at), metaTitle: preview.meta_title || '',
        metaDescription: preview.meta_description || '',
        categoryIds: (preview.categories || []).map(category => categories.find(item => item.slug === category.slug)?.id).filter(Boolean),
        tagIds: (preview.tags || []).map(tag => tags.find(item => item.slug === tag.slug)?.id).filter(Boolean)
      })
      setEditorOpen(true)
      setError('')
    } catch (err) {
      fail(err)
    }
  }

  const setField = (field, value) => setPost(current => ({ ...current, [field]: value }))
  const setSelected = (field, event) => setField(field, Array.from(event.target.selectedOptions, option => option.value))

  const savePost = async (event) => {
    event.preventDefault()
    if (!post.title.trim() || !post.content.trim()) {
      setError('Title and HTML content are required.')
      return
    }
    setSaving(true)
    try {
      const payload = {
        title: post.title.trim(), slug: post.slug.trim(), excerpt: post.excerpt.trim(), content: post.content,
        coverImage: post.coverImage.trim(), status: post.status,
        publishedAt: post.status === 'published' ? (post.publishedAt ? new Date(post.publishedAt).toISOString() : new Date().toISOString()) : null,
        metaTitle: post.metaTitle.trim(), metaDescription: post.metaDescription.trim(),
        categoryIds: post.categoryIds, tagIds: post.tagIds
      }
      const endpoint = post.id ? `/api/blog/admin/posts/${post.id}` : '/api/blog/admin/posts'
      await fetch(endpoint, { method: post.id ? 'PUT' : 'POST', headers, body: JSON.stringify(payload) }).then(readJson)
      notify(post.id ? 'Post updated.' : 'Post created.')
      setEditorOpen(false)
      setPost(emptyPost)
      await loadAll()
    } catch (err) {
      fail(err)
    } finally {
      setSaving(false)
    }
  }

  const deleteItem = async (endpoint, label) => {
    if (!confirm(`Delete this ${label}? This cannot be undone.`)) return
    try {
      await fetch(endpoint, { method: 'DELETE', headers: { Authorization: `Bearer ${token()}` } }).then(readJson)
      notify(`${label[0].toUpperCase()}${label.slice(1)} deleted.`)
      await loadAll()
    } catch (err) { fail(err) }
  }

  const updateComment = async (id, status) => {
    try {
      await fetch(`/api/blog/admin/comments/${id}`, { method: 'PUT', headers, body: JSON.stringify({ status }) }).then(readJson)
      notify('Comment updated.')
      await loadAll()
    } catch (err) { fail(err) }
  }

  const addTaxonomy = async (event, kind) => {
    event.preventDefault()
    const data = new FormData(event.currentTarget)
    try {
      await fetch(`/api/blog/admin/${kind}`, { method: 'POST', headers, body: JSON.stringify({ name: data.get('name'), description: data.get('description') }) }).then(readJson)
      event.currentTarget.reset()
      notify(`${kind === 'categories' ? 'Category' : 'Tag'} saved.`)
      await loadAll()
    } catch (err) { fail(err) }
  }

  if (!user || user.role !== 'admin') {
    return <div className="profile-card" style={{ textAlign: 'center' }}><h3>Admin Access Required</h3><p>You don't have permission to manage blog content.</p></div>
  }

  return (
    <div>
      {message && <div className="form-status success" style={{ display: 'block', marginBottom: 20 }}>{message}</div>}
      {error && <div className="form-status error" style={{ display: 'block', marginBottom: 20 }}>{error}</div>}

      <div className="admin-tabs">
        {['posts', 'categories', 'tags', 'comments'].map(item => <button key={item} className={`admin-tab ${tab === item ? 'active' : ''}`} onClick={() => setTab(item)}>{item[0].toUpperCase() + item.slice(1)}</button>)}
      </div>

      {tab === 'posts' && <>
        <div className="blog-admin-toolbar"><button className="btn btn-primary btn-sm" onClick={openNew}><i className="fas fa-plus" /> New Post</button></div>
        {editorOpen && <form className="profile-card blog-editor" onSubmit={savePost}>
          <div className="blog-editor-heading"><h3>{post.id ? 'Edit post' : 'New post'}</h3><button type="button" className="btn btn-outline btn-sm" onClick={() => setEditorOpen(false)}>Close</button></div>
          <div className="blog-editor-grid">
            <label>Title<input required value={post.title} onChange={event => setField('title', event.target.value)} /></label>
            <label>Slug<input placeholder="generated from title if left blank" value={post.slug} onChange={event => setField('slug', event.target.value)} /></label>
            <label>Status<select value={post.status} onChange={event => setField('status', event.target.value)}><option value="draft">Draft</option><option value="published">Published</option><option value="scheduled">Scheduled</option></select></label>
            <label>Publish date{post.status === 'published' ? <input type="datetime-local" value={post.publishedAt} onChange={event => setField('publishedAt', event.target.value)} /> : <input disabled placeholder="Set when publishing" />}</label>
            <label className="blog-editor-wide">Cover image URL<input type="url" value={post.coverImage} onChange={event => setField('coverImage', event.target.value)} /></label>
            <label className="blog-editor-wide">Excerpt<textarea rows="3" value={post.excerpt} onChange={event => setField('excerpt', event.target.value)} /></label>
            <label className="blog-editor-wide">HTML content<textarea required className="blog-html-editor" rows="14" placeholder="<p>Your article content...</p>" value={post.content} onChange={event => setField('content', event.target.value)} /></label>
            <label>SEO title<input value={post.metaTitle} onChange={event => setField('metaTitle', event.target.value)} /></label>
            <label>SEO description<textarea rows="3" value={post.metaDescription} onChange={event => setField('metaDescription', event.target.value)} /></label>
            <label>Categories<select multiple value={post.categoryIds} onChange={event => setSelected('categoryIds', event)}>{categories.map(item => <option key={item.id} value={item.id}>{item.name}</option>)}</select></label>
            <label>Tags<select multiple value={post.tagIds} onChange={event => setSelected('tagIds', event)}>{tags.map(item => <option key={item.id} value={item.id}>{item.name}</option>)}</select></label>
          </div>
          <button className="btn btn-primary" disabled={saving}>{saving ? 'Saving…' : 'Save post'}</button>
        </form>}
        <div className="admin-table-wrapper"><table className="admin-table"><thead><tr><th>Title</th><th>Status</th><th>Date</th><th>Views</th><th>Actions</th></tr></thead><tbody>
          {posts.map(item => <tr key={item.id}><td>{item.title}</td><td>{item.status}</td><td>{item.published_at ? new Date(item.published_at).toLocaleDateString() : '—'}</td><td>{item.views || 0}</td><td><div className="admin-actions"><button className="btn btn-sm btn-outline" onClick={() => openEdit(item)}>Edit</button><button className="btn btn-sm btn-outline" onClick={() => deleteItem(`/api/blog/admin/posts/${item.id}`, 'post')}>Delete</button></div></td></tr>)}
          {!posts.length && <tr><td colSpan="5" style={{ textAlign: 'center', padding: 20 }}>No posts yet.</td></tr>}
        </tbody></table></div>
      </>}

      {['categories', 'tags'].includes(tab) && <>
        <form className="profile-card form-inline" style={{ maxWidth: 680, marginBottom: 20 }} onSubmit={event => addTaxonomy(event, tab)}><input name="name" required placeholder={`${tab === 'categories' ? 'Category' : 'Tag'} name`} />{tab === 'categories' && <input name="description" placeholder="Description" />}<button className="btn btn-primary btn-sm">Add</button></form>
        <div className="admin-table-wrapper"><table className="admin-table"><thead><tr><th>Name</th><th>Slug</th><th>Posts</th><th>Actions</th></tr></thead><tbody>{(tab === 'categories' ? categories : tags).map(item => <tr key={item.id}><td>{item.name}</td><td>{item.slug}</td><td>{item.post_count || 0}</td><td><button className="btn btn-sm btn-outline" onClick={() => deleteItem(`/api/blog/admin/${tab}/${item.id}`, tab.slice(0, -1))}>Delete</button></td></tr>)}</tbody></table></div>
      </>}

      {tab === 'comments' && <div className="admin-table-wrapper"><table className="admin-table"><thead><tr><th>Name</th><th>Content</th><th>Status</th><th>Date</th><th>Actions</th></tr></thead><tbody>{comments.map(item => <tr key={item.id}><td>{item.name}</td><td>{item.content}</td><td>{item.status}</td><td>{new Date(item.created_at).toLocaleDateString()}</td><td><div className="admin-actions">{item.status !== 'approved' && <button className="btn btn-sm btn-outline" onClick={() => updateComment(item.id, 'approved')}>Approve</button>}<button className="btn btn-sm btn-outline" onClick={() => updateComment(item.id, 'spam')}>Spam</button><button className="btn btn-sm btn-outline" onClick={() => deleteItem(`/api/blog/admin/comments/${item.id}`, 'comment')}>Delete</button></div></td></tr>)}{!comments.length && <tr><td colSpan="5" style={{ textAlign: 'center', padding: 20 }}>No comments yet.</td></tr>}</tbody></table></div>}
    </div>
  )
}
